import { Injectable } from '@angular/core';
import {
	ComponentController,
	Notification,
	filterEmpty,
	filterNull,
} from '@lycoris-nubila/ngx-lycoris';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isEqual, uniq, without } from 'lodash-es';
import { Observable, combineLatest, of } from 'rxjs';
import {
	distinctUntilChanged,
	first,
	map,
	pluck,
	shareReplay,
	startWith,
	switchMap,
	tap,
} from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import { NotificationDomain } from 'application/common/model/notification-domain';
import { AppSharedApplicationPort } from 'application/common/port/app-shared-application.port';
import { MessageApplicationPort } from 'application/message/port/message-application.port';
import { UserApplicationPort } from 'user/port/user-application.port';
import { IUser } from 'user/user';
import { IUserUsageStats } from 'user/user-usage-stats';

import { AppService } from '../app.service';
import { AppChatState, AppChatStore } from './app-chat.store';
import { ChatMessageModel } from './chat-message/chat-message.model';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class AppChatController extends ComponentController<AppChatState> {
	public sourceUser$: Observable<IUser>;
	public participants$: Observable<IUser[]>;
	public appChatWidthPx$: Observable<number>;
	public messages$: Observable<ChatMessageModel[]>;
	public userUsageStats$: Observable<IUserUsageStats>;

	private _targetUser$: Observable<IUser>;
	private _participantsId$: Observable<string[]>;

	constructor(
		store: AppChatStore,
		private _appService: AppService,
		private _userApplicationPort: UserApplicationPort,
		private _messageApplicationPort: MessageApplicationPort,
		private _appSharedApplicationPort: AppSharedApplicationPort
	) {
		super(store);

		this._initObservables();
		this._initListeners();
	}

	public closeChat() {
		this._appService.setAppChatUserId(null);
	}

	public startVisio(content: string) {
		this._participantsId$
			.pipe(
				filterEmpty(),
				first(),
				tap((participantsId) =>
					this._messageApplicationPort.sendMessage({
						visioSessionId: uuid(),
						participantsId,
						content,
					})
				)
			)
			.subscribe();
	}

	public sendMessage(content: string) {
		this._participantsId$
			.pipe(
				filterEmpty(),
				first(),
				tap((participantsId) =>
					this._messageApplicationPort.sendMessage({
						participantsId,
						content,
					})
				)
			)
			.subscribe();
	}

	public toggleChat(): void {
		this._appService.toggleChat();
	}

	public updateUserId(userId: string): void {
		this.store.update({ userId });
	}

	private _initObservables() {
		this.sourceUser$ = this._userApplicationPort
			.observeSessionUser()
			.pipe(shareReplay({ refCount: true, bufferSize: 1 }));

		this._targetUser$ = this.select('userId').pipe(
			filterEmpty(),
			switchMap((userId) => this._userApplicationPort.observeUserById(userId)),
			map((data) => data.data),
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this._participantsId$ = combineLatest([
			this.sourceUser$.pipe(filterNull()),
			this._targetUser$.pipe(filterNull()),
		]).pipe(
			map(([sourceUser, targetUser]) =>
				without(
					sourceUser.isCoachee
						? [targetUser.id, ...sourceUser.observersId]
						: [targetUser.id, ...targetUser.observersId],
					sourceUser.id
				)
			),
			map(uniq),
			distinctUntilChanged(isEqual),
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.participants$ = this._participantsId$.pipe(
			switchMap((participantsId) =>
				this._userApplicationPort.observeUserByIds(participantsId)
			),
			map((data) => data.data),
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.messages$ = combineLatest([
			this.sourceUser$.pipe(
				filterNull(),
				pluck('id'),
				distinctUntilChanged(isEqual)
			),
			this.sourceUser$.pipe(
				filterNull(),
				map((sourceUser) => sourceUser.isCoachee)
			),
			this._participantsId$.pipe(filterEmpty(), distinctUntilChanged(isEqual)),
		]).pipe(
			switchMap(([sourceUserId, isSourceUserCoachee, participantsId]) =>
				this._messageApplicationPort
					.observeMessages({
						authorId: sourceUserId,
						isAuthorCoachee: isSourceUserCoachee,
						participantsId,
					})
					.pipe(
						tap((messages) =>
							this._messageApplicationPort.markMessagesRead(messages.map((m) => m.id))
						),
						map((messages) =>
							messages.map(
								(message) =>
									({
										message,
										notAParticipant$: of(message.authorId).pipe(
											map(
												(authorId) =>
													!participantsId.includes(authorId) && authorId !== sourceUserId
											)
										),
										mine$: message.author$.pipe(
											map((author) => author?.id === sourceUserId)
										),
									} as ChatMessageModel)
							)
						)
					)
			),
			startWith([]),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.userUsageStats$ = this._appSharedApplicationPort
			.observeUserUsageStats()
			.pipe(
				map((data) => data.data),
				shareReplay({ refCount: true, bufferSize: 1 })
			);

		this.appChatWidthPx$ = this._appService.appChatWidthPx$;
	}

	private _initListeners() {
		combineLatest([
			this.sourceUser$.pipe(filterNull(), distinctUntilChanged(isEqual)),
			this._participantsId$.pipe(filterEmpty(), distinctUntilChanged(isEqual)),
		])
			.pipe(
				switchMap(([sourceUser, participantsId]) =>
					this._messageApplicationPort.fetchMessages({
						authorId: sourceUser.id,
						participantsId,
					})
				),
				untilDestroyed(this)
			)
			.subscribe();
	}
}
